//Libraries
import { FC, useState, useEffect } from 'react';
import { Dialog, Grid } from '@mui/material';
import { Button } from '@cb/apricot-react';

//Interfaces & Typing
import { IAIModel } from 'src/types/org/ai/aiInterface';
import { Draft } from 'src/types/common';

//Processing
import { ChangeManager } from 'src/generic/utils/form';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { formatAddress } from 'src/generic/component/address';

//Components
import { FieldRow, Panel, Title, RadioLabel, IconButton, FieldNote, Highlight, Divider } from 'src/generic/component';
import { BooleanRadio, Input, Radio, Dropdown } from 'src/generic/component/form';
import { AiSearch } from 'src/org/component/orgSearch';

//Icons & Styling
import FlagIcon from '@mui/icons-material/Flag';

//Providers
import { useReferences } from 'src/services';


interface IAiFieldProps {
  readOnly?: boolean;
  item: Draft<IAIModel>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IAIModel>;
  hints?: any;
  showDiff?: boolean;
  showProtected?: boolean;
  onUpdate?: (address: Draft<IAIModel>) => void;
}

export const AiFields: FC<IAiFieldProps> = ({ hints, original, showProtected, item: initial, readOnly, errors, onUpdate }) => {
  const { countries } = useReferences() || {}
  const [isMergeSearchOpen, setMergeSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<any>({});

  const merge = hints?.merge || {};
  const zip = merge.address?.zip5;
  const postalCd = merge.address?.intlPostalCd;
  const countryIsoCd = merge.address?.countryIsoCd;

  useEffect(() => {
    setSearchParams({
      name: merge.name,
      countryIsoCd: countryIsoCd,
      zip: zip,
      postalCd: postalCd,
      aiCode: merge.aiCode
    });
  }, [hints]);

  /**
     * Form Variables and Monitoring Funcationality
     * 
     * This is the place where most additions and modifications will occur for the form
     * in addition to the rendering block.
     */
  const {
    aiCode,
    initialIngestionPathCd,
    apEligibilityInd,
    psatEligibilityInd,
    satEligibilityInd,
    clepEligibilityInd,
    ssdEligibilityInd,
    annualReviewInd,
    stateHomeSchoolInd,
    crossPrgSecHoldInd,
    aiCategoryCd,
    aiStatusCd,
    aiLevelCd,
    aiMergedCode
  } = initial || {};

  const showMerge = original?.aiStatusCd === 2 && aiStatusCd === 1;

  const { getError } = ErrorManager(errors);
  const { format, formatOption, formatBoolean, isChanged, getChangeColor } = ChangeManager<IAIModel>(original || {}, initial);

  const changed = (updates: Draft<IAIModel>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  const ingestionOptions = [{ key: 1, label: 'Normal' }, { key: 2, label: 'State Contract' }];
  const aiStatusOptions = [{ key: 2, label: 'Active' }, { key: 1, label: 'Inactive' }];
  const aiCategoryOptions = [{ key: 1, label: 'AI' }, { key: 2, label: 'AIA' }];
  const aiLevelOptions = [{ key: 1, label: 'Level 1: Receive Score Reports' }, { key: 2, label: 'Level 2: Administer Tests & Receive Score Reports' }];

  return <>
    <Panel>
      <Title>AI Information</Title>
      {aiCode &&
        <FieldRow>
          <strong>Updating AI: {aiCode}</strong>
        </FieldRow>
      }
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">AI Code Type</RadioLabel>
            {(original && isChanged('aiCategoryCd')) &&
              <div><i className={getChangeColor('aiCategoryCd')}>Current: {formatOption('aiCategoryCd', aiCategoryOptions)}</i></div>
            }
            <Radio name='aiCategoryCd' readOnly={readOnly} value={aiCategoryCd} error={getError('aiCategoryCd')}
              options={aiCategoryOptions}
              onChange={(val: any) => { changed({ aiCategoryCd: val }); }}
            />
          </FieldRow>
          <FieldRow style={{ borderBottom: '' }}>
            <RadioLabel variant="body1">AI Status</RadioLabel>
            {(original && isChanged('aiStatusCd')) &&
              <div><i className={getChangeColor('aiStatusCd')}>Current: {formatOption('aiStatusCd', aiStatusOptions)}</i></div>
            }
            <Radio name='aiStatusCd' readOnly={readOnly || !aiCode} value={aiStatusCd} error={getError('aiStatusCd')}
              options={aiStatusOptions}
              onChange={(val: any) => {
                changed({
                  aiStatusCd: val,
                  aiMergedCode: val === 2 ? undefined : aiMergedCode
                });
              }}
            />
          </FieldRow>

          {showMerge &&
            <>
              <Grid container style={{ marginBottom: '10px' }}>
                <Grid item>
                  <FieldRow>
                    <RadioLabel>
                      <div style={{ display: 'inline-block', marginRight: '5px' }}>Merged AI Code:</div>
                      <Highlight style={{ display: 'inline-block', marginRight: '5px' }}>{aiMergedCode || 'No AI Selected'}</Highlight>
                      <div style={{ lineHeight: 1, marginBottom: '8px' }}><FieldNote>(If you are merging, use the search to choose this AI.)</FieldNote></div>
                    </RadioLabel>
                    {(original && isChanged('aiMergedCode')) &&
                      <div style={{ marginBottom: '5px' }}><i className={getChangeColor('aiMergedCode')}>Current: {format('aiMergedCode')}</i></div>
                    }
                    {!readOnly &&
                      ((aiMergedCode) ?
                        <>
                          {aiMergedCode &&
                            <IconButton onClick={() => {
                              changed({ aiMergedCode: undefined });
                            }}>Change</IconButton>
                          }
                        </> :
                        <>
                          <Button onClick={() => {
                            setMergeSearchOpen(true);
                          }}>Search AIs</Button>
                          <Dialog fullWidth={true} maxWidth={'md'} open={isMergeSearchOpen}>
                            <div style={{ padding: '20px' }}>
                              <FieldRow>
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Input onChange={(e) => {
                                      setSearchParams({ ...searchParams, name: e.target.value });
                                    }} placeholder='Name' value={searchParams.name} />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Input onChange={(e) => {
                                      setSearchParams({ ...searchParams, text: e.target.value });
                                    }} placeholder='Text Search' value={searchParams.text} />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Input onChange={(e) => {
                                      setSearchParams({ ...searchParams, aiCode: e.target.value });
                                    }} placeholder='AI Code' value={searchParams.aiCode} />
                                  </Grid>
                                  <Grid item xs={4}>
                                    {countries && <Dropdown values={countries.map((option) => ({ key: option.value, label: option.label }))}
                                      onChange={(e) => {
                                        setSearchParams({ countryIsoCd: e.target.value, zip: undefined, postalCd: undefined });
                                      }}
                                      value={searchParams.countryIsoCd} />}
                                  </Grid>
                                  {searchParams.countryIsoCd &&
                                    <Grid item xs={4}>
                                      {searchParams.countryIsoCd === 'US' ?
                                        <Input onChange={(e) => {
                                          setSearchParams({ ...searchParams, zip: e.target.value, postalCd: undefined });
                                        }} placeholder='Zip' value={searchParams.zip} /> :
                                        <Input onChange={(e) => {
                                          setSearchParams({ ...searchParams, zip: undefined, postalCd: e.target.value });
                                        }} placeholder='Postal Code' value={searchParams.postalCd} />
                                      }
                                    </Grid>
                                  }
                                </Grid>
                              </FieldRow>
                              <Divider />
                              <AiSearch
                                params={{
                                  aicode: searchParams.aiCode,
                                  name: searchParams.name,
                                  zip: searchParams.zip,
                                  intl_postal_cd: searchParams.postalCd,
                                  iso: searchParams.countryIsoCd,
                                  text: searchParams.text
                                }}
                                onSelect={(match) => {
                                  changed({ aiMergedCode: match.aiCode });
                                  setMergeSearchOpen(false);
                                }}
                              />
                              <Button onClick={() => {
                                setMergeSearchOpen(false);
                              }}>Close</Button>
                            </div>
                          </Dialog>
                        </>)
                    }
                  </FieldRow>
                </Grid>
                <Grid item container sm justifyContent={'flex-start'}>
                  <Grid item xs container direction="column" spacing={2}>
                  </Grid>
                  <Grid item>
                    {hints?.merge &&
                      <FieldNote>
                        User Submitted:
                        <div style={{ lineHeight: '18px' }}>
                          {merge.aiCode && <div>AI Code - {merge.aiCode}</div>}
                          {merge.name &&
                            <>
                              <div>{merge.name}</div>
                              {formatAddress(merge.address)}
                            </>
                          }
                        </div>
                      </FieldNote>
                    }
                  </Grid>
                </Grid>
              </Grid>
              <Divider />
            </>
          }
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">AI Level</RadioLabel>
            {(original && isChanged('aiLevelCd')) &&
              <div><i className={getChangeColor('aiLevelCd')}>Current: {formatOption('aiLevelCd', aiLevelOptions)}</i></div>
            }
            <Radio name='aiLevelCd' readOnly={readOnly} value={aiLevelCd} error={getError('aiLevelCd')}
              options={aiLevelOptions}
              onChange={(val: any) => { changed({ aiLevelCd: val }); }}
            />
          </FieldRow>
          {showProtected &&
            <FieldRow>
              <RadioLabel variant="body1">Initial Ingestion Path</RadioLabel>
              {(original && isChanged('initialIngestionPathCd')) &&
                <div><i className={getChangeColor('initialIngestionPathCd')}>Current: {formatOption('initialIngestionPathCd', ingestionOptions)}</i></div>
              }
              <Radio name='initialIngestionPathCd' readOnly={readOnly} value={initialIngestionPathCd} error={getError('initialIngestionPathCd')}
                options={ingestionOptions}
                onChange={(val: any) => { changed({ initialIngestionPathCd: val }); }}
              />
            </FieldRow>
          }
        </Grid>
      </Grid>

      <FieldRow><strong>Eligibility Flags <FlagIcon /> </strong></FieldRow>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">AP Eligibility</RadioLabel>
            {(original && isChanged('apEligibilityInd')) &&
              <div><i className={getChangeColor('apEligibilityInd')}>Current: {formatBoolean('apEligibilityInd')}</i></div>
            }
            <BooleanRadio name='apEligibilityInd' readOnly={readOnly} value={apEligibilityInd} error={getError('apEligibilityInd')}
              onChange={(value) => {
                changed({ apEligibilityInd: value });
              }} />
          </FieldRow>
          <FieldRow>
            <RadioLabel variant="body1">PSAT Eligibility</RadioLabel>
            {(original && isChanged('psatEligibilityInd')) &&
              <div><i className={getChangeColor('psatEligibilityInd')}>Current: {formatBoolean('psatEligibilityInd')}</i></div>
            }
            <BooleanRadio name='psatEligibilityInd' readOnly={readOnly} value={psatEligibilityInd} error={getError('psatEligibilityInd')}
              onChange={(value) => {
                changed({ psatEligibilityInd: value });
              }} />
          </FieldRow>
          <FieldRow>
            <RadioLabel variant="body1">SAT Eligibility</RadioLabel>
            {(original && isChanged('satEligibilityInd')) &&
              <div><i className={getChangeColor('satEligibilityInd')}>Current: {formatBoolean('satEligibilityInd')}</i></div>
            }
            <BooleanRadio name='satEligibilityInd' readOnly={readOnly} value={satEligibilityInd} error={getError('satEligibilityInd')}
              onChange={(value) => {
                changed({ satEligibilityInd: value });
              }} />
          </FieldRow>
          <FieldRow>
            <RadioLabel variant="body1">CLEP Eligibility</RadioLabel>
            {(original && isChanged('clepEligibilityInd')) &&
              <div><i className={getChangeColor('clepEligibilityInd')}>Current: {formatBoolean('clepEligibilityInd')}</i></div>
            }
            <BooleanRadio name='clepEligibilityInd' readOnly={readOnly} value={clepEligibilityInd} error={getError('clepEligibilityInd')}
              onChange={(value) => {
                changed({ clepEligibilityInd: value });
              }} />
          </FieldRow>

        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">SSD Eligibility</RadioLabel>
            {(original && isChanged('ssdEligibilityInd')) &&
              <div><i className={getChangeColor('ssdEligibilityInd')}>Current: {formatBoolean('ssdEligibilityInd')}</i></div>
            }
            <BooleanRadio name='ssdEligibilityInd' readOnly={readOnly} value={ssdEligibilityInd} error={getError('ssdEligibilityInd')}
              onChange={(value) => {
                changed({ ssdEligibilityInd: value });
              }} />
          </FieldRow>
          <FieldRow>
            <RadioLabel variant="body1">State Home School Reporting Eligibility</RadioLabel>
            {(original && isChanged('stateHomeSchoolInd')) &&
              <div><i className={getChangeColor('stateHomeSchoolInd')}>Current: {formatBoolean('stateHomeSchoolInd')}</i></div>
            }
            <BooleanRadio name='stateHomeSchoolInd' readOnly={readOnly} value={stateHomeSchoolInd} error={getError('stateHomeSchoolInd')}
              onChange={(value) => {
                changed({ stateHomeSchoolInd: value });
              }} />
          </FieldRow>
          <FieldRow>
            <RadioLabel variant="body1">Annual Review</RadioLabel>
            {(original && isChanged('annualReviewInd')) &&
              <div><i className={getChangeColor('annualReviewInd')}>Current: {formatBoolean('annualReviewInd')}</i></div>
            }
            <BooleanRadio name='annualReviewInd' readOnly={readOnly} value={annualReviewInd} error={getError('annualReviewInd')}
              onChange={(value) => {
                changed({ annualReviewInd: value });
              }} />
          </FieldRow>

          <FieldRow>
            <RadioLabel variant="body1">Cross Program Security Hold</RadioLabel>
            {(original && isChanged('crossPrgSecHoldInd')) &&
              <div><i className={getChangeColor('crossPrgSecHoldInd')}>Current: {formatBoolean('crossPrgSecHoldInd')}</i></div>
            }
            <BooleanRadio name='crossPrgSecHoldInd' readOnly={readOnly} value={crossPrgSecHoldInd} error={getError('crossPrgSecHoldInd')}
              onChange={(value) => {
                changed({ crossPrgSecHoldInd: value });
              }} />
          </FieldRow>

        </Grid>
      </Grid>
    </Panel >
  </>;
};